import React, { useState } from "react";
import { useSelector, useDispatch } from "react-redux";

import { Pane } from "src/app/components/Pane/Pane";
import {
  PaneTopbar,
  PaneTopbarLeft,
  PaneTopbarToolbar,
} from "src/app/components/Pane/PaneTopbar/PaneTopbar";
import IDHFormattedMessage from "src/app/components/IDHFormattedMessage/IDHFormattedMessage";
import { useShowPane } from "src/hooks/useShowPane";
import SearchHistoryList from "./SearchHistoryPaneList";
import { ReactComponent as HistoryIcon } from "src/images/time-schedule.svg";
import { ReactComponent as FilterIcon } from "src/images/filter.svg";
import { ReactComponent as TickIcon } from "src/images/tick.svg";
import {
  Dropdown,
  DropdownMenu,
  DropdownMenuItem,
} from "src/app/components/Dropdown/Dropdown";
import { Button } from "src/app/components/Button/Button";
import { setSearchHistoryPaneOpen } from "src/redux/project/projectActions";
import { RootState } from "src/redux/reducers";

import "./SearchHistoryPane.scss";

type DateRangeOption = {
  days: number;
  months: number;
  messageId: string;
  defaultMessage: string;
};

const dateRangeOptions: DateRangeOption[] = [
  { days: 7, months: 1, messageId: "ws_week", defaultMessage: "week" },
  { days: 14, months: 2, messageId: "ws_weeks", defaultMessage: "weeks" },
  { days: 30, months: 30, messageId: "ws_days", defaultMessage: "days" },
  { days: 90, months: 3, messageId: "ws_months", defaultMessage: "months" },
  { days: 180, months: 6, messageId: "ws_months", defaultMessage: "months" },
];

interface SearchHistoryPaneProps {
  projectId: string;
}

function SeachHistoryPaneContent({ projectId }: SearchHistoryPaneProps) {
  const dispatch = useDispatch();
  const [showPane, hidePane] = useShowPane(() => {
    dispatch(setSearchHistoryPaneOpen(false, null));
  });

  const [selectedDays, setSelectedDays] = useState<number>(30);

  // Calculate fromDate based on selected option
  const date = new Date();
  date.setDate(date.getDate() - selectedDays);
  const fromDate = date.toISOString().split("T")[0]; // Format: YYYY-MM-DD

  const getSelectedLabel = (days: number) => {
    const option = dateRangeOptions.find((opt) => opt.days === days);
    if (!option) return null;

    return (
      <>
        {option.months}{" "}
        <IDHFormattedMessage
          id={option.messageId}
          defaultMessage={option.defaultMessage}
        />
      </>
    );
  };

  return (
    <Pane
      showPane={showPane}
      hidePane={hidePane}
      width={420}
      className="search-history-pane"
    >
      <PaneTopbar>
        <PaneTopbarLeft>
          <HistoryIcon className="search-history-pane__icon" />
          <span className="text-h4-medium">
            <IDHFormattedMessage
              id="ws_search_history"
              defaultMessage="Search history"
            />
          </span>
        </PaneTopbarLeft>
        <PaneTopbarToolbar hidePane={hidePane}>
          <Dropdown>
            {(isOpen, setIsOpen) => (
              <>
                <Button
                  size="medium"
                  variant="white-with-light-grey-border"
                  onClick={() => setIsOpen((prev) => !prev)}
                  data-qa="search-history-date-range-button"
                >
                  <FilterIcon className="search-history-pane__dropdown-icon" />
                  <span>
                    <IDHFormattedMessage id="ws_last" defaultMessage="Last:" />{" "}
                    {getSelectedLabel(selectedDays)}
                  </span>
                </Button>
                <DropdownMenu isOpen={isOpen}>
                  {dateRangeOptions.map((option) => (
                    <DropdownMenuItem
                      key={option.days}
                      onClick={() => {
                        setSelectedDays(option.days);
                        setIsOpen(false);
                      }}
                    >
                      <span>{getSelectedLabel(option.days)}</span>
                      {option.days === selectedDays && <TickIcon />}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenu>
              </>
            )}
          </Dropdown>
        </PaneTopbarToolbar>
      </PaneTopbar>
      <div className="search-history-pane__content">
        <SearchHistoryList fromDate={fromDate} projectId={projectId} />
      </div>
    </Pane>
  );
}

export default function SearchHistoryPane() {
  const searchHistoryPaneState = useSelector(
    (state: RootState) => state.projectReducer.searchHistoryPaneState,
  );

  if (!searchHistoryPaneState.isOpen || !searchHistoryPaneState.projectId) {
    return null;
  }

  return (
    <SeachHistoryPaneContent projectId={searchHistoryPaneState.projectId} />
  );
}
